import { CrmPreloader } from "./CrmPreloader";
import { WorkspacePage } from "../layout/WorkspacePage";
import { MyTasksPage } from "../tasks/MyTasksPage";
import { projectTitle } from "../deals/projectIdentity";
import { HelpPage } from "../help/HelpPage";
import { FinancialAnalytics } from "../finance/FinancialAnalytics";
import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import {
  CanAccess,
  Navigate,
  CustomRoutes,
  localStorageStore,
  Resource,
} from "ra-core";
import { useEffect, useMemo, lazy, Suspense } from "react";
import { Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";

import companies from "../companies";
import { MobileCompanyList } from "../companies/MobileCompanyList";
import contacts from "../contacts";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import deals from "../deals";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { SignupPage } from "../login/SignupPage";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/supabase";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import sales from "../sales";
import { SalesListMobile } from "../sales/SalesListMobile";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { TasksPage } from "../tasks/TasksPage.tsx";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";

const defaultStore = localStorageStore(undefined, "CRM");
const AdminToolsPage = lazy(() => import("../adminTools/AdminToolsPage"));
const AdminToolsRoute = () => (
  <CanAccess
    resource="admin_tools"
    action="list"
    accessDenied={<Navigate to="/access-denied" />}
  >
    <Suspense fallback={<p className="p-6">Загрузка админ-меню…</p>}>
      <AdminToolsPage />
    </Suspense>
  </CanAccess>
);
const WorkProjectsPage = lazy(() => import("../workProjects/WorkProjectsPage"));
const WorkProjectsRoute = () => (
  <Suspense fallback={<p className="p-6">Загрузка рабочего проекта…</p>}>
    <WorkProjectsPage />
  </Suspense>
);

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {LabeledValue[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} darkModeLogo - Logo shown in dark mode and on the auth pages. Must be an imported asset, an absolute URL, or a data URI — never a route-relative path like "./logos/x.svg", which breaks on nested routes such as /oauth/consent (issue #291).
 * @param {string} lightModeLogo - Logo shown in light mode. Same rule as darkModeLogo: imported asset, absolute URL, or data URI only.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         darkModeLogo="https://example.com/logo-dark.svg"
 *         lightModeLogo="https://example.com/logo-light.svg"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        companySectors,
        currency,
        dealCategories,
        dealPipelineStatuses,
        dealStages,
        noteStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  // Configuration and logos load through useConfigurationLoader after login.
  // Branding must never hold authentication open or duplicate Storage requests.
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        return authProvider.login(params);
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        return authProvider.handleCallback(params);
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <ResponsiveAdmin
      dataProvider={dataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={StartPage}
      requireAuth
      disableTelemetry
      {...rest}
    />
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  return (
    <Admin
      loading={CrmPreloader}
      layout={props.layout ?? Layout}
      dashboard={props.dashboard ?? Dashboard}
      {...props}
    >
      <CustomRoutes noLayout>
        <Route path={SignupPage.path} element={<SignupPage />} />
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        <Route
          path={ForgotPasswordPage.path}
          element={<ForgotPasswordPage />}
        />
        <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
      </CustomRoutes>

      <CustomRoutes>
        <Route path="/workspace" element={<WorkspacePage />} />
        <Route path="/admin-tools" element={<AdminToolsRoute />} />
        <Route path="/my-tasks" element={<MyTasksPage />} />
        <Route path="/analytics" element={<FinancialAnalytics />} />
        <Route path="/work-projects" element={<WorkProjectsRoute />} />
        <Route path="/work-projects/:id" element={<WorkProjectsRoute />} />
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route
          path={SettingsPage.path}
          element={
            <CanAccess resource="configuration" action="edit">
              <SettingsPage />
            </CanAccess>
          }
        />
        <Route
          path={ImportPage.path}
          element={
            <CanAccess
              resource="import"
              action="create"
              accessDenied={<Navigate to="/access-denied" />}
            >
              <ImportPage />
            </CanAccess>
          }
        />
        <Route path={ChangelogPage.path} element={<ChangelogPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route
          path="/configuration"
          element={
            <CanAccess resource="configuration" action="edit">
              <SettingsPage />
            </CanAccess>
          }
        />
      </CustomRoutes>
      <Resource name="deals" {...deals} recordRepresentation={projectTitle} />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="project_lookup" recordRepresentation={projectTitle} />
      <Resource name="tasks" list={TasksPage} />
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        networkMode: "offlineFirst",
      },
      mutations: {
        networkMode: "offlineFirst",
      },
    },
  });
  const asyncStoragePersister = createAsyncStoragePersister({
    key: "rovero.configuration-query-cache.v2",
    storage: localStorage,
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: asyncStoragePersister,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) =>
            query.queryKey[0] === "configuration" &&
            query.meta?.persist !== false &&
            query.state.status === "success",
        },
      }}
    >
      <Admin
        loading={CrmPreloader}
        queryClient={queryClient}
        layout={props.layout ?? MobileLayout}
        dashboard={props.dashboard ?? MobileDashboard}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route path={SignupPage.path} element={<SignupPage />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route path="/workspace" element={<WorkspacePage />} />
          <Route path="/admin-tools" element={<AdminToolsRoute />} />
          <Route path="/my-tasks" element={<MyTasksPage />} />
          <Route
            path={ProfilePage.path}
            element={
              <>
                <MobileHeader title="Мой профиль" />
                <MobileContent>
                  <ProfilePage />
                </MobileContent>
              </>
            }
          />
          <Route path="/analytics" element={<FinancialAnalytics />} />
          <Route path="/work-projects" element={<WorkProjectsRoute />} />
          <Route path="/work-projects/:id" element={<WorkProjectsRoute />} />
          <Route
            path={SettingsPageMobile.path}
            element={<SettingsPageMobile />}
          />
          <Route path={ChangelogPage.path} element={<ChangelogPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route
            path="/configuration"
            element={
              <CanAccess resource="configuration" action="edit">
                <SettingsPage />
              </CanAccess>
            }
          />
        </CustomRoutes>
        <Resource
          name="contacts"
          create={contacts.create}
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource
          name="companies"
          {...companies}
          list={MobileCompanyList}
          show={CompanyShow}
        />
        <Resource
          name="sales"
          create={sales.create}
          edit={() => (
            <>
              <MobileHeader title="Редактирование пользователя" />
              <MobileContent>
                <sales.edit />
              </MobileContent>
            </>
          )}
          list={SalesListMobile}
          recordRepresentation={(record) =>
            [record.first_name, record.last_name].filter(Boolean).join(" ")
          }
        />
        <Resource name="deals" {...deals} recordRepresentation={projectTitle} />
        <Resource name="project_lookup" recordRepresentation={projectTitle} />
        <Resource name="tasks" list={TasksPage} />
      </Admin>
    </PersistQueryClientProvider>
  );
};
